import '../onboarding.css'

const ONBOARDING_COPY = [
  '이 존재는 공격할수록 커집니다.',
  '무기는 단 하나 — 이 존재를 묘사하는 당신의 문장입니다.',
  "문장에서 뽑힌 '성질'이 자물쇠 홈에 맞으면 격리 성공입니다.",
] as const

function visualForStep(step: number): string {
  if (step === 0) {
    return `
      <div class="onboarding-visual onboarding-visual--growth" aria-hidden="true">
        <span class="onboarding-impact"></span>
        <span class="onboarding-orb"></span>
      </div>
    `
  }

  if (step === 1) {
    return `
      <div class="onboarding-visual onboarding-visual--forge" aria-hidden="true">
        <span class="onboarding-sentence-line"></span>
        <span class="onboarding-key">
          <span class="onboarding-key__bow"></span>
          <span class="onboarding-key__stem"></span>
          <span class="onboarding-key__tooth onboarding-key__tooth--one"></span>
          <span class="onboarding-key__tooth onboarding-key__tooth--two"></span>
        </span>
        <span class="onboarding-forge-glow"></span>
      </div>
    `
  }

  return `
    <div class="onboarding-visual onboarding-visual--lock" aria-hidden="true">
      <div class="onboarding-traits">
        <span></span><span></span><span></span>
      </div>
      <div class="onboarding-lockplate">
        <span class="onboarding-groove"></span>
        <span class="onboarding-groove"></span>
        <span class="onboarding-groove"></span>
      </div>
    </div>
  `
}

function progressDots(activeStep: number): string {
  return ONBOARDING_COPY.map((_, index) => `
    <span class="onboarding-progress__dot${index === activeStep ? ' is-active' : ''}"
      aria-hidden="true"></span>
  `).join('')
}

/**
 * Opens the three-card metaphor onboarding and returns an idempotent cleanup.
 * The callback runs once after the final card or a skip action.
 */
export function showOnboarding(onComplete: () => void): () => void {
  const overlay = document.createElement('section')
  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
  let step = 0
  let finished = false

  overlay.className = 'onboarding-overlay'
  overlay.dataset.testid = 'onboarding'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-labelledby', 'onboarding-copy')

  const render = (): void => {
    const isLast = step === ONBOARDING_COPY.length - 1
    overlay.innerHTML = `
      <button class="onboarding-skip" type="button" data-testid="onboarding-skip">
        전체 건너뛰기
      </button>
      <article class="onboarding-card" data-testid="onboarding-step" data-step="${step + 1}">
        <div class="onboarding-progress" aria-label="${ONBOARDING_COPY.length}장 중 ${step + 1}번째">
          ${progressDots(step)}
        </div>
        ${visualForStep(step)}
        <p class="onboarding-copy" id="onboarding-copy">${ONBOARDING_COPY[step]}</p>
        <button class="onboarding-next" type="button" data-testid="onboarding-next">
          ${isLast ? '격리 시작' : '다음'}
          <span aria-hidden="true">→</span>
        </button>
      </article>
      <p class="onboarding-key-hint" aria-hidden="true">클릭 · Enter · →</p>
    `
    overlay.querySelector<HTMLButtonElement>('[data-testid="onboarding-next"]')?.focus()
  }

  const finish = (): void => {
    if (finished) return
    finished = true
    document.removeEventListener('keydown', handleKeydown)
    overlay.remove()
    previouslyFocused?.focus()
    onComplete()
  }

  const advance = (): void => {
    if (step >= ONBOARDING_COPY.length - 1) {
      finish()
      return
    }
    step += 1
    render()
  }

  const handleKeydown = (event: KeyboardEvent): void => {
    const target = event.target instanceof Element ? event.target : null
    if (event.key === 'Escape') {
      event.preventDefault()
      finish()
      return
    }

    if (event.key === 'Tab') {
      event.preventDefault()
      const selector = target?.closest('[data-testid="onboarding-skip"]')
        ? '[data-testid="onboarding-next"]'
        : '[data-testid="onboarding-skip"]'
      overlay.querySelector<HTMLButtonElement>(selector)?.focus()
      return
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      advance()
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (target?.closest('[data-testid="onboarding-skip"]')) {
        finish()
        return
      }
      advance()
    }
  }

  overlay.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null
    if (target?.closest('[data-testid="onboarding-skip"]')) {
      finish()
      return
    }
    advance()
  })
  document.addEventListener('keydown', handleKeydown)
  document.body.append(overlay)
  render()

  return finish
}
